// סשן GoTrue (11 §1): access+refresh ב-localStorage (web-session-across-windows) — כניסה אחת לכל הלשוניות והחלונות; כניסה/יציאה
// בלשונית אחרת מגיעה באירוע storage. רענון כשנותרו < 5 דקות; refresh יחיד בו-זמנית בתוך לשונית (בין לשוניות — חלון החפיפה של GoTrue).
// התפוגה (7 ימים בלי פעילות / 30 סך הכול) והרוטציה נאכפות ב-GoTrue (infra/compose.yml), לא כאן.
// התפקיד נגזר מה-JWT בכל טעינה (app_metadata.role) — רק להסתרת תפריט; השרת אוכף.
import { useSyncExternalStore } from 'react';
import type { Role } from '@aibify/core';

export type Session = {
  accessToken: string;
  refreshToken: string;
  /** exp של ה-access token, בשניות */
  exp: number;
  /** platform-mfa-totp: רמת ההזדהות מה-JWT — aal2 אחרי קוד מאפליקציית האימות (או כשאין גורם: aal1 ומספיק) */
  aal: 'aal1' | 'aal2';
  user: { id: string; email: string; nameHe: string; role: Role };
};

const KEY = 'aibify.session';
export const REFRESH_MARGIN_S = 5 * 60;
const ROLES: Role[] = ['admin', 'creator', 'member'];

export class AuthExpired extends Error {
  constructor() {
    super('auth-expired');
  }
}
export class LoginFailed extends Error {
  constructor(public readonly textHe: string) {
    super(textHe);
  }
}

function b64urlJson(part: string): Record<string, unknown> {
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
  const bin = atob(b64);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
}

export function decodeJwt(token: string): Record<string, unknown> {
  const p = token.split('.')[1];
  if (!p) throw new Error('bad-jwt');
  return b64urlJson(p);
}

export function sessionFromTokens(accessToken: string, refreshToken: string): Session {
  const p = decodeJwt(accessToken);
  const app = (p.app_metadata ?? {}) as Record<string, unknown>;
  const meta = (p.user_metadata ?? {}) as Record<string, unknown>;
  const role = ROLES.includes(app.role as Role) ? (app.role as Role) : 'member';
  const email = typeof p.email === 'string' ? p.email : '';
  return {
    accessToken,
    refreshToken,
    exp: typeof p.exp === 'number' ? p.exp : 0,
    aal: p.aal === 'aal2' ? 'aal2' : 'aal1',
    user: { id: String(p.sub ?? ''), email, nameHe: typeof meta.name_he === 'string' ? meta.name_he : email, role },
  };
}

let current: Session | null = read();
const listeners = new Set<() => void>();
/** הסשן האחרון נגמר ברענון שנכשל (פקיעה בשרת) — /login מציג "הכניסה פגה". מתאפס בכל כניסה/יציאה. */
let endedByExpiry = false;
const notify = () => listeners.forEach((l) => l());

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    // התפקיד לא נשמר כאמת — נגזר מחדש מה-JWT
    return sessionFromTokens(s.accessToken, s.refreshToken);
  } catch {
    return null;
  }
}

function write(s: Session | null, expired = false) {
  current = s;
  endedByExpiry = expired;
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify({ accessToken: s.accessToken, refreshToken: s.refreshToken }));
    else localStorage.removeItem(KEY);
  } catch {}
  notify();
}

// לשונית אחרת נכנסה / רעננה / יצאה (key=null: localStorage.clear()) — אותו מפתח, ולכן אותו סשן כאן; RequireSession מנווט לבד
// (בלי טיפוסי DOM: הקובץ מקומפל גם בהקשר node של ה-workspace)
type StorageWin = { addEventListener(type: 'storage', l: (e: { key: string | null }) => void): void };
(globalThis as { window?: StorageWin }).window?.addEventListener('storage', (e) => {
  if (e.key !== KEY && e.key !== null) return;
  current = read();
  notify();
});

export const getSession = () => current;
/** בלי סשן — לא נוגע (כדי לא למחוק את סימן הפקיעה שהרענון השאיר). */
export const clearSession = () => {
  if (current) write(null);
};
/** האם הסשן נגמר בפקיעה (רענון נדחה) ולא ביציאה — ל-`/login?reason=session_expired`. */
export const sessionEndedByExpiry = () => endedByExpiry;

export function subscribeSession(l: () => void): () => void {
  listeners.add(l);
  return () => void listeners.delete(l);
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribeSession, () => current);
}

async function tokenRequest(grant: 'password' | 'refresh_token', body: unknown): Promise<Session> {
  const r = await fetch(`/auth/v1/token?grant_type=${grant}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const b = (await r.json().catch(() => ({}))) as { access_token?: string; refresh_token?: string };
  if (!r.ok || !b.access_token || !b.refresh_token) {
    if (grant === 'refresh_token') throw new AuthExpired();
    throw new LoginFailed(r.status === 400 || r.status === 401 || r.status === 422 ? 'דוא"ל או סיסמה שגויים. נסה שוב.' : `ההתחברות נכשלה (${r.status}). נסה שוב בעוד רגע.`);
  }
  return sessionFromTokens(b.access_token, b.refresh_token);
}

export async function login(email: string, password: string): Promise<Session> {
  let s: Session;
  try {
    s = await tokenRequest('password', { email, password });
  } catch (e) {
    if (e instanceof LoginFailed) throw e;
    throw new LoginFailed('אין חיבור לשרת ההזדהות. בדוק את הרשת ונסה שוב.');
  }
  write(s);
  return s;
}

/**
 * 26 §1.2: קישור כניסה חד-פעמי — magic link של GoTrue. אותו JWT ואותו refresh כמו בסיסמה; נשמר ב-localStorage בדיוק כמו login
 * ומחליף כניסה קיימת. כשל (פג / נוצל / לא קיים) → false, בלי לרמוז למה. אחרי הצלחה — דיווח שימוש ל-api (ממתין עד 5 שניות; לינק ישיר).
 */
export async function loginWithLink(tokenHash: string): Promise<boolean> {
  let s: Session;
  try {
    const r = await fetch('/auth/v1/verify', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type: 'magiclink', token_hash: tokenHash }) });
    const b = (await r.json().catch(() => ({}))) as { access_token?: string; refresh_token?: string };
    if (!r.ok || !b.access_token || !b.refresh_token) return false;
    s = sessionFromTokens(b.access_token, b.refresh_token);
  } catch {
    return false;
  }
  write(s);
  // platform-direct-link: ממתינים (עד 5 שניות) — אם זה לינק ישיר, השרת קושר את הסשן לפני ש-MfaGate קורא את mfa/status, כך שמסך
  // הקוד לא מהבהב. t = ה-token_hash שכבר נוצל ב-verify. כשל לא עוצר את הכניסה (מגיעים למסך הקוד כרגיל).
  try {
    await fetch('/api/auth/link-used', { method: 'POST', headers: { authorization: `Bearer ${s.accessToken}`, 'content-type': 'application/json' }, body: JSON.stringify({ t: tokenHash }), signal: AbortSignal.timeout(5000) });
  } catch {
    /* best-effort */
  }
  return true;
}

/**
 * platform-password-recovery: "שכחתי סיסמה" — POST /auth/v1/recover ישירות ל-GoTrue. 200 → sent (גם לאימייל לא רשום — GoTrue עונה
 * 200 {} ולא מגלה); 429 over_email_send_rate_limit → rate_limited; כל דבר אחר (SMTP נכשל, רשת) → failed — המסך מציג את אותו כרטיס.
 */
export async function requestPasswordReset(email: string): Promise<'sent' | 'rate_limited' | 'failed'> {
  try {
    const r = await fetch('/auth/v1/recover', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email }) });
    if (r.ok) return 'sent';
    return r.status === 429 ? 'rate_limited' : 'failed';
  } catch {
    return 'failed';
  }
}

/** הסשן שיצא מ-verify של קישור השחזור — בזיכרון המודול בלבד עד שהסיסמה התקבלה (לא ב-localStorage). */
let recoverySession: Session | null = null;

/**
 * platform-password-recovery: /login/reset — verify {type:'recovery', token_hash} פעם אחת (ה-token חד-פעמי; הסשן מוחזק בזיכרון),
 * ואז PUT /auth/v1/user {password}. רק כשהסיסמה התקבלה — write(session) (מחליף כניסה קיימת) + דיווח audit ל-api (best-effort).
 * verify נכשל → link_expired; PUT נדחה (422 weak_password / קצרה מ-GOTRUE_PASSWORD_MIN_LENGTH) → rejected, ואפשר לנסות שוב
 * עם אותו סשן בלי קישור חדש.
 */
export async function resetPasswordWithLink(tokenHash: string, password: string): Promise<'ok' | 'link_expired' | 'rejected'> {
  if (!recoverySession) {
    try {
      const r = await fetch('/auth/v1/verify', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type: 'recovery', token_hash: tokenHash }) });
      const b = (await r.json().catch(() => ({}))) as { access_token?: string; refresh_token?: string };
      if (!r.ok || !b.access_token || !b.refresh_token) return 'link_expired';
      recoverySession = sessionFromTokens(b.access_token, b.refresh_token);
    } catch {
      return 'link_expired';
    }
  }
  const s = recoverySession;
  try {
    const r = await fetch('/auth/v1/user', { method: 'PUT', headers: { authorization: `Bearer ${s.accessToken}`, 'content-type': 'application/json' }, body: JSON.stringify({ password }) });
    if (!r.ok) return 'rejected';
  } catch {
    return 'rejected';
  }
  recoverySession = null;
  write(s);
  void fetch('/api/auth/password-reset', { method: 'POST', headers: { authorization: `Bearer ${s.accessToken}`, 'content-type': 'application/json' }, body: '{}' }).catch(() => {});
  return 'ok';
}

/** platform-mfa-totp: GoTrue החזיר סשן חדש (אימות קוד → aal2) — מחליף את הנוכחי, כמו כניסה. */
export function adoptTokens(accessToken: string, refreshToken: string): Session {
  const s = sessionFromTokens(accessToken, refreshToken);
  write(s);
  return s;
}

/** בדיקות בלבד: שכחת הסשן שהוחזק מ-verify. */
export const _resetRecoveryState = () => {
  recoverySession = null;
};

let inflight: Promise<Session> | null = null;

export function refresh(): Promise<Session> {
  const s = current;
  if (!s) return Promise.reject(new AuthExpired());
  inflight ??= tokenRequest('refresh_token', { refresh_token: s.refreshToken })
    .then((n) => {
      write(n);
      return n;
    })
    .catch((e) => {
      // לשונית אחרת רעננה ראשונה (רוטציה) — הטוקן החדש כבר במפתח; ממשיכים איתו במקום לצאת
      const other = read();
      if (other && other.refreshToken !== s.refreshToken) {
        current = other;
        notify();
        return other;
      }
      write(null, true);
      throw e instanceof AuthExpired ? e : new AuthExpired();
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** access token תקף ל-5 דקות לפחות; אחרת מרענן קודם. */
export async function getAccessToken(): Promise<string> {
  const s = current;
  if (!s) throw new AuthExpired();
  if (s.exp - Date.now() / 1000 < REFRESH_MARGIN_S) return (await refresh()).accessToken;
  return s.accessToken;
}

export function logout() {
  const s = current;
  write(null);
  if (!s) return;
  // platform-push-notifications: המנוי של הסשן הזה נמחק לפני סיום הסשן (keepalive — שורד את המעבר לדף הכניסה), וה-push
  // subscription בדפדפן מבוטל. אם זה נכשל — השרת ממילא לא שולח לסשן שכבר לא קיים.
  const auth = { authorization: `Bearer ${s.accessToken}` };
  void fetch('/api/push/subscriptions/current', { method: 'DELETE', headers: auth, keepalive: true })
    .catch(() => {})
    .finally(() => void fetch('/auth/v1/logout', { method: 'POST', headers: auth }).catch(() => {}));
  // (טיפוס מינימלי: auth.ts נבדק גם בהקשר בלי lib של DOM)
  type Sw = { getRegistration(p: string): Promise<{ pushManager: { getSubscription(): Promise<{ unsubscribe(): Promise<boolean> } | null> } } | undefined> };
  void (globalThis.navigator as { serviceWorker?: Sw } | undefined)?.serviceWorker
    ?.getRegistration('/push-sw.js')
    .then((r) => r?.pushManager.getSubscription())
    .then((sub) => sub?.unsubscribe())
    .catch(() => {});
}

export const roleAtLeast = (role: Role | undefined, min: Role) => !!role && ROLES.indexOf(role) <= ROLES.indexOf(min);
